/**
 * Singleton Supabase client — pinned на `globalThis`.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * АРХИТЕКТУРНЫЙ ИНВАРИАНТ (CONTRACT.md §8 Client lifecycle):
 *   На один runtime — РОВНО ОДИН SupabaseClient. Точка.
 *
 *   Хранение singleton в module-scoped `let` НЕ ДОСТАТОЧНО:
 *     • dynamic `await import(...)` в tsx/Vite/SSR может загрузить модуль второй
 *       раз — и тогда `let _client` создастся повторно. session живёт в client A,
 *       RPC идёт через client B — сервер видит role=anon. Недетерминированно.
 *
 *   Поэтому singleton хранится на `globalThis[SINGLETON_KEY]`. Даже если
 *   client.ts загружен N раз через разные ESM-graphs, getSupabaseClient()
 *   возвращает ОДИН и тот же объект.
 *
 *   Если ты видишь warning "double module load" — найди dynamic import в
 *   коде и замени на static. Это смок-тест архитектуры.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Env-переменные:
 *   VITE_SUPABASE_URL — обязательно
 *   VITE_SUPABASE_ANON_KEY — обязательно
 *
 * `service_role` ключ используется только в Edge Functions / seed-скриптах —
 * никогда не попадает в клиентскую сборку.
 */
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "./types-db";

const SINGLETON_KEY = "__cafeOsSupabaseClient_v1";
const MODULE_LOAD_KEY = "__cafeOsClientModuleLoadCount";

/**
 * Auth namespace flag — позволяет приложениям-гостям использовать ОТДЕЛЬНЫЙ
 * localStorage key для auth-сессии, изолированный от staff-сессии того же
 * пользователя в том же браузере.
 *
 * Кейс: владелец кафе залогинен как owner в /admin/, открывает /menu/table/X.
 * Без namespace гостевой `ensureAuthUser()` ловит staff-сессию и регистрирует
 * этого "гостя" в session_guests под staff uid. Если у него ДВА устройства,
 * оба сворачиваются в одного "гостя" → корзина показывает что один человек
 * заказал всё.
 *
 * Решение: guest-app в `main.tsx` ДО первого `getSupabaseClient()` вызова
 * выставляет `globalThis.__cafeOsAuthNamespace = "guest"`. Тогда client
 * создаётся с `storageKey = "sb-cafe-os-guest-auth-token"` — полностью
 * отдельная auth-сессия, fresh anon-signin при первом обращении.
 *
 * Должно быть выставлено ТОЛЬКО до создания singleton. После создания
 * изменение игнорируется (singleton кэшируется).
 */
const AUTH_NAMESPACE_KEY = "__cafeOsAuthNamespace";

interface CafeOsGlobal {
  [SINGLETON_KEY]?: SupabaseClient<Database>;
  [MODULE_LOAD_KEY]?: number;
  [AUTH_NAMESPACE_KEY]?: string;
}

const g = globalThis as unknown as CafeOsGlobal;

// Бьёмся при каждой загрузке этого модуля — детектор duplicate ESM graph.
// Ссылка на process через globalThis cast — kitchen-app (browser, нет @types/node)
// иначе ловит TS2591.
const _proc = (globalThis as unknown as {
  process?: { env?: Record<string, string | undefined> };
}).process;

g[MODULE_LOAD_KEY] = (g[MODULE_LOAD_KEY] ?? 0) + 1;
if (g[MODULE_LOAD_KEY]! > 1 && _proc?.env?.DB_DEBUG) {
  console.error(
    `[@cafe-os/db client.ts] WARN: module loaded ${g[MODULE_LOAD_KEY]}× (expect 1). ` +
    `Singleton сохраняется через globalThis, но это сигнал что где-то есть ` +
    `dynamic import / разные specifier'ы. Найди и замени на static import.`,
  );
}

/** In-memory storage для Node (test-flow, scripts) — без window/localStorage. */
class NodeMemoryStorage {
  private store: Record<string, string> = {};
  getItem(key: string): string | null {
    return Object.prototype.hasOwnProperty.call(this.store, key) ? this.store[key] : null;
  }
  setItem(key: string, value: string): void {
    this.store[key] = value;
  }
  removeItem(key: string): void {
    delete this.store[key];
  }
}

export function getSupabaseClient(): SupabaseClient<Database> {
  const cached = g[SINGLETON_KEY];
  if (cached) return cached;

  const url = readEnv("VITE_SUPABASE_URL");
  const anon = readEnv("VITE_SUPABASE_ANON_KEY");
  if (!url || !anon) {
    throw new Error(
      "[@cafe-os/db] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY не заданы. " +
      "Проверь .env.local (или Vercel env).",
    );
  }
  const isBrowser = typeof window !== "undefined";
  const namespace = g[AUTH_NAMESPACE_KEY];
  // Если приложение задало namespace (например guest-app), кладём auth в
  // отдельный localStorage key. Иначе используем Supabase default
  // (`sb-<project-ref>-auth-token`), который шарится между staff-приложениями.
  const storageKey = namespace ? `sb-cafe-os-${namespace}-auth-token` : undefined;

  const client = createClient<Database>(url, anon, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: isBrowser,
      flowType: "pkce",
      storage: isBrowser ? undefined : new NodeMemoryStorage(),
      ...(storageKey ? { storageKey } : {}),
    },
    realtime: {
      params: { eventsPerSecond: 10 },
    },
  });

  g[SINGLETON_KEY] = client;
  return client;
}

/** Вернёт клиент без создания. Полезно для опционального префикса в обёртках. */
export function maybeSupabase(): SupabaseClient<Database> | null {
  return g[SINGLETON_KEY] ?? null;
}

/**
 * Устанавливает auth-namespace для singleton Supabase client. Должно быть
 * вызвано ДО первого `getSupabaseClient()`. Использовать в гостевом
 * приложении в `main.tsx` ПЕРВОЙ строкой:
 *
 *   import { setAuthNamespace } from "@cafe-os/db";
 *   setAuthNamespace("guest");
 *
 * После этого guest-app будет использовать `sb-cafe-os-guest-auth-token`
 * вместо staff-shared `sb-<project>-auth-token` — отдельная anon-сессия
 * на устройство, не пересекается с залогиненным staff юзером.
 */
export function setAuthNamespace(namespace: string): void {
  if (g[SINGLETON_KEY]) {
    console.warn(
      `[@cafe-os/db] setAuthNamespace("${namespace}") called AFTER client creation — ignored. ` +
      `Must be called before first getSupabaseClient(). Likely cause: imported too late in main.tsx.`,
    );
    return;
  }
  g[AUTH_NAMESPACE_KEY] = namespace;
}

/**
 * ⚠️ TEST-ONLY: сбрасывает singleton. НЕ ВЫЗЫВАТЬ в app code.
 * Нужно когда integration test переключает env между test-tenants.
 */
export function __resetSupabaseClientForTests(): void {
  delete g[SINGLETON_KEY];
}

function readEnv(key: string): string | undefined {
  const meta = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  if (meta && typeof meta[key] === "string") return meta[key];
  // Используем _proc (см. выше) вместо `process` — для browser-сборок без @types/node.
  if (_proc?.env && typeof _proc.env[key] === "string") {
    return _proc.env[key];
  }
  return undefined;
}
